/* Built In Imports */

/* External Imports */
import { Box, Flex, Text } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { nanoid } from 'nanoid';
/* Internal Imports */
/* Components */
import SimpleButton from '@components/HNI/Components/Buttons/SimpleButton';
import SectionTitle from '@components/HNI/Components/Headings/SectionTitle';
import JsonTableUI from '@components/HNI/Components/Tables/JsonTableUI';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';

/* Services */
/**
 * Impact
 * @param {Object} sectionContent
 * @returns Impact
 */
const Impact = ({ sectionContent, region, lang }) => {
  const fundingReq = sectionContent.find(el => el.fieldId === 'funding-req');
  const seekers = sectionContent.find(el => el.fieldId === 'seekers');
  const jsonTable = sectionContent.find(el => el._modelApiKey === 'json_table');
  const fullAmt = sectionContent.find(el => el.fieldId === 'full-amt');
  const logistics = sectionContent.find(el => el.fieldId === 'logistics');
  const note = sectionContent.find(el => el.fieldId === 'note');
  const title = sectionContent.find(el => el._modelApiKey === 'section_title');
  const fontSize = { base: '16px', md: '16px' };
  const button = sectionContent.find(el => el._modelApiKey === 'simple_button');
  const card_group = sectionContent.find(el => el._modelApiKey === 'card_custom_card_group');

  return (
    <Box height="100%" px="20px">
      <SectionTitle titleObj={title} mt={{ base: '0', lg: '3rem' }} w="100%" color="#ffffff" />
      <StructuredTextParser
        region={region}
        textAlign="center"
        lang={lang}
        color="#fff"
        fontSize={fontSize}
        lineHeight="30px"
        mt="40px"
        mb={{ base: '30px', md: '40px' }}
        fontFamily="FedraSansStd-book"
        str={render(fundingReq?.body)}
      />
      <StructuredTextParser
        region={region}
        textAlign="center"
        lang={lang}
        color="#FFAE27"
        fontSize={{ base: '30px', md: '48px' }}
        lineHeight="38px"
        fontFamily="FedraSansStd-medium"
        fontWeight="700"
        mt="0"
        sx={{
          'p::first-letter': {
            fontFamily: "'FedraSerifDisplay', serif",
            fontWeight: 700,
          },
        }}
        str={render(fullAmt?.body)}
      />
      <StructuredTextParser
        region={region}
        textAlign="center"
        color="#fff"
        fontSize={{ base: '16px', md: '18px' }}
        lineHeight="30px"
        fontFamily="FedraSansStd-book"
        lang={lang}
        mt="28px"
        str={render(logistics?.body)}
      />
      <StructuredTextParser
        region={region}
        textAlign="center"
        color="#FFAE27"
        fontSize={{ base: '16px', md: '18px' }}
        lineHeight="30px"
        mt="0"
        fontFamily="FedraSansStd-medium"
        lang={lang}
        str={render(seekers?.body)}
      />
      <Box maxW={275} w="100%" mx="auto">
        <StructuredTextParser
          region={region}
          mt="20px"
          textAlign="center"
          color="#fff"
          fontSize="13px"
          lineHeight="24px"
          fontFamily="FedraSansStd-book"
          sx={{
            strong: {
              fontWeight: '500',
              fontFamily: 'FedraSansStd-medium',
            },
          }}
          maxW="275px"
          mx="auto"
          lang={lang}
          str={render(note?.body)}
          mb="20px"
        />
      </Box>
      {jsonTable && (
        <Box
          bg="#FCF6E2"
          maxW="650px"
          mx="auto"
          width="100%"
          mt={{ base: '3.5rem', md: '3.5rem', lg: '3.5rem' }}
          mb={{ base: '20px', md: '20px' }}
          rounded="6px"
          px="20px"
        >
          <JsonTableUI sectionContent={jsonTable} />
        </Box>
      )}
     {card_group && (
      <Box
        w={{ base: '100%' }}
        maxW={1000}
        m={{ base: '0 auto', md: '0 auto' }}
        p={{ base: '55px 0 25px 0' }}
        textAlign={{ base: 'center' }}
        display={{ base: 'flex', md: 'flex' }}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Box
          display={{ base: 'flex' }}
          gap={{ base: '2rem', md: '1.5rem', lg: '1.2rem' }}
          flexDirection={{ base: 'column', sm: 'row' }}
          justifyContent={{ base: 'flex-start', md: 'space-around' }}
          alignItems={{ base: 'center', sm: 'flex-start' }}
          w="100%"
          flexWrap="wrap"
        >
          {card_group?.cards?.map((data, index) => {
            return (
              <Flex
                alignItems={{ base: 'center', md: 'center' }}
                justifyContent="center"
                flexDirection="column"
                p={{ base:'10px 10px', md:'0 10px'}}
                key={nanoid()}
                flex={1}
                w={{ base: 280, md: 320 }}
              >
                <LazyLoadImageComponent
                  width="100%"
                  loading="lazy"
                  alt={data?.thumbnail?.alt || ''}
                  title={data?.thumbnail?.title}
                  maxW="200px"
                  maxH="200px"
                  w="100px"
                  h="auto"
                  src={data?.thumbnail?.url}
                  m={{ base: '0 auto 22px auto' }}
                  borderRadius={
                    sectionContent.style === 'default' ? '50%' : ''
                  }
                />
                <Text
                  as="h6"
                  color="#ffffff"
                  fontFamily="FedraSerifAStdBook"
                  fontSize={{ base: '22px' }}
                  m={{ base: '10px auto 0px auto', lg: '1rem auto 0' }}
                  w={{ base: '100%', md: '234px', lg: '100%' }}
                  lineHeight="30px"
                  textAlign={'center'}
                  maxW={index === 2 ? '190px' : '270px'}
                >
                  {data?.title}
                </Text>
                <Box maxW="290px">
                  <StructuredTextParser
                    fontFamily="FedraSansStd-book"
                    region={region}
                    textAlign="center"
                    paddingX={{ base: '1.3rem', md: '0', lg: '0' }}
                    lang={lang}
                    color="#ffffff"
                    lineHeight="24px"
                    str={render(data.description)}
                  />
                </Box>
              </Flex>
            );
          })}
        </Box>
      </Box>
     )}
      <SimpleButton
        style={button}
        mx="auto"
        marginTop="30px"
        color="#28231E"
        // _hover={{
        //   color: '#FFFFFF',
        // }}
      />
    </Box>
  );
};

export default Impact;
